import { Link } from "react-router-dom";
import { MapPin, UtensilsCrossed, Quote, Heart, ChefHat, Tv } from "lucide-react";
import { IMAGES, RESTAURANT } from "../data/site";
import { Reveal } from "./Reveal";

const facts = [
  { icon: MapPin, label: "Roots", value: "Born & raised in the Bronx, NY" },
  { icon: ChefHat, label: "Cooking Since", value: "Age 9 — three generations of women" },
  { icon: Heart, label: "Moved to Virginia", value: "2015, to be near her grandkids" },
  { icon: Tv, label: "As Seen On", value: "The Hampton Roads Show" },
];

export const AboutChefRainey = () => {
  return (
    <section data-testid="about-chef-page" className="bg-cream">
      {/* Header */}
      <div className="relative pt-36 pb-20 md:pt-44 md:pb-28 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-beige to-cream" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div>
            <Reveal>
              <span className="font-heading text-gold-dark uppercase tracking-[0.3em] text-sm">
                — Our Story
              </span>
            </Reveal>
            <Reveal delay={0.05}>
              <h1 className="mt-4 font-script text-gold text-6xl md:text-7xl leading-[0.9]">
                Meet Chef Rainey
              </h1>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-6 font-body text-lg md:text-xl text-charcoal/75 leading-relaxed">
                Julie Rainey has been cooking since she was nine years old — the little girl
                in a kitchen full of women, who wanted a taste of everything that hit the pot.
                Today she pours that same love into every plate at Rainey's Soul Food.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="mt-8 flex flex-wrap gap-4">
                <Link
                  data-testid="about-view-menu-btn"
                  to="/#menu"
                  className="inline-flex items-center gap-2.5 bg-burgundy text-cream px-8 py-4 rounded-full font-body font-semibold tracking-wide hover:bg-burgundy-dark transition-all duration-300 hover:scale-[1.03]"
                >
                  <UtensilsCrossed size={18} /> View the Menu
                </Link>
                <a
                  data-testid="about-directions-btn"
                  href={RESTAURANT.directionsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2.5 border border-gold text-charcoal px-8 py-4 rounded-full font-body font-semibold tracking-wide hover:bg-gold hover:text-cream transition-all duration-300"
                >
                  <MapPin size={18} /> Visit Us
                </a>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.1} className="relative">
            <div className="absolute -inset-3 border border-gold/50 rounded-[2rem] translate-x-4 translate-y-4" />
            <img
              src={IMAGES.owner}
              alt="Chef Julie Rainey in her kitchen"
              className="relative rounded-[2rem] w-full h-[460px] md:h-[600px] object-cover object-top shadow-[0_20px_40px_rgba(0,0,0,0.2)]"
            />
            <div className="absolute bottom-5 left-5 bg-cream/95 backdrop-blur-sm px-5 py-3 rounded-2xl shadow-lg">
              <p className="font-script text-2xl text-burgundy leading-none">Chef Julie Rainey</p>
              <p className="font-body text-[0.65rem] uppercase tracking-[0.2em] text-gold-dark mt-1">
                Owner
              </p>
            </div>
          </Reveal>
        </div>
      </div>

      {/* Facts */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 pb-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {facts.map((f, i) => (
            <Reveal
              key={f.label}
              delay={i * 0.05}
              className="bg-white rounded-3xl p-6 border border-beige shadow-[0_4px_20px_rgba(122,28,34,0.05)]"
            >
              <f.icon className="text-burgundy" size={26} />
              <p className="mt-4 font-body text-[0.7rem] uppercase tracking-widest text-gold-dark">
                {f.label}
              </p>
              <p className="mt-1 font-heading text-lg text-charcoal font-medium">{f.value}</p>
            </Reveal>
          ))}
        </div>
      </div>

      {/* Story */}
      <div className="max-w-3xl mx-auto px-6 md:px-12 py-20 md:py-28">
        <Reveal>
          <div className="space-y-6 font-body text-lg text-charcoal/80 leading-relaxed">
            <p>
              "I grew up with my mother, my grandmother and my great-grandmother," Rainey remembers.
              "I was basically the taster — anything that dropped, I wanted it." Those women, and
              their kitchens, shaped a cook who has never stopped feeding the people she loves.
            </p>
            <p>
              A New Yorker at heart, Chef Rainey moved from the Bronx to Virginia in 2015 to be
              closer to her grandkids. She kept catering, and started selling her all-natural
              beverages — including a beloved non-alcoholic ginger beer — at the Buckroe Beach
              Farmer's Market. Before long, her family encouraged her to plant roots of her own.
            </p>
            <p>
              She describes her cooking simply: <span className="text-burgundy font-semibold">soul
              food with a Caribbean flair</span>. The menu changes with her mood and the market —
              she wakes up and decides what she'll cook that day. But some things are always worth
              the trip: her fall-apart <span className="text-burgundy font-semibold">oxtails</span> and
              her <span className="text-burgundy font-semibold">smothered turkey wings</span> have a
              following all their own, and earned her a feature on The Hampton Roads Show.
            </p>
            <p>
              With a degree in social work, Rainey sees the restaurant as more than a kitchen. "Having
              a restaurant is like having that neighborhood beauty shop," she says. "You get the social
              in." She runs it with her family, and dreams of one day hiring local students to learn
              the business.
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <blockquote className="mt-14 relative bg-burgundy text-cream rounded-3xl p-9 md:p-12">
            <Quote className="text-gold/40 absolute top-6 left-6" size={40} />
            <p className="relative font-heading text-2xl md:text-3xl italic leading-snug">
              "When you walk in, you'll feel the comfort of home. You will be reminded of your
              childhood, or your grandmother's kitchen."
            </p>
            <footer className="mt-6 font-script text-gold-light text-3xl">— Chef Rainey</footer>
          </blockquote>
        </Reveal>
      </div>
    </section>
  );
};
